<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import BusinessDay from './BusinessDay.vue';
import BusinessTimeInput from './BusinessTimeInput.vue';
import {
  timeSlotParse,
  timeSlotTransform,
  defaultTimeSlot,
  timeZoneOptions,
  getTime,
  timeInMinutes,
  slotWorkingIntervals,
} from '../helpers/businessHour';

const props = defineProps({ inbox: { type: Object, required: true } });
const { t, locale } = useI18n();
const store = useStore();
const DEFAULT_TIMEZONE = 'America/Los_Angeles';
const isBusinessHoursEnabled = ref(false);
const unavailableMessage = ref('');
const timeZone = ref(DEFAULT_TIMEZONE);
const timeSlots = ref([]);
const lunchEnabled = ref(false);
const lunchStart = ref(getTime(12, 0));
const lunchEnd = ref(getTime(13, 0));
const timeZones = timeZoneOptions();
const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);
const lunchStartMinute = computed(() => timeInMinutes(lunchStart.value));
const lunchEndMinute = computed(() => timeInMinutes(lunchEnd.value));
const isLunchValid = computed(
  () => !lunchEnabled.value || lunchStartMinute.value < lunchEndMinute.value
);
const effectiveLunchStart = computed(() =>
  lunchEnabled.value && isLunchValid.value ? lunchStartMinute.value : null
);
const effectiveLunchEnd = computed(() =>
  lunchEnabled.value && isLunchValid.value ? lunchEndMinute.value : null
);
const hasError = computed(() => {
  if (!isBusinessHoursEnabled.value) return false;
  if (!isLunchValid.value) return true;
  return timeSlots.value.some(
    slot =>
      slot.enabled &&
      (!slot.valid ||
        !slotWorkingIntervals(
          slot,
          effectiveLunchStart.value,
          effectiveLunchEnd.value
        ).length)
  );
});
const dayNames = computed(() => {
  const formatter = new Intl.DateTimeFormat(locale.value.replace('_', '-'), {
    weekday: 'long',
    timeZone: 'UTC',
  });
  return Array.from({ length: 7 }, (_, day) => {
    const name = formatter.format(new Date(Date.UTC(2024, 0, 7 + day)));
    return name.charAt(0).toLocaleUpperCase() + name.slice(1);
  });
});
const isRichEditorEnabled = computed(
  () =>
    ![INBOX_TYPES.TWILIO, INBOX_TYPES.TWITTER, INBOX_TYPES.FB].includes(
      props.inbox.channel_type
    )
);
watch(
  () => props.inbox,
  inbox => {
    const slots = timeSlotParse(inbox.working_hours || []).map(slot => ({
      ...slot,
      enabled: Boolean(slot.from && slot.to),
    }));
    isBusinessHoursEnabled.value = inbox.working_hours_enabled || false;
    unavailableMessage.value = inbox.out_of_office_message || '';
    timeSlots.value = slots.length
      ? slots
      : defaultTimeSlot.map(slot => ({ ...slot, enabled: false }));
    timeZone.value = inbox.timezone || DEFAULT_TIMEZONE;
    lunchEnabled.value = Number.isInteger(inbox.lunch_start_minute);
    lunchStart.value = lunchEnabled.value
      ? getTime(
          Math.floor(inbox.lunch_start_minute / 60),
          inbox.lunch_start_minute % 60
        )
      : getTime(12, 0);
    lunchEnd.value = lunchEnabled.value
      ? getTime(
          Math.floor(inbox.lunch_end_minute / 60),
          inbox.lunch_end_minute % 60
        )
      : getTime(13, 0);
  },
  { immediate: true }
);
const onSlotUpdate = (day, data) => {
  timeSlots.value = timeSlots.value.map(slot =>
    slot.day === day ? data : slot
  );
};
const updateInbox = async () => {
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      working_hours_enabled: isBusinessHoursEnabled.value,
      out_of_office_message: unavailableMessage.value,
      working_hours: timeSlotTransform(timeSlots.value),
      timezone: timeZone.value,
      lunch_start_minute: lunchEnabled.value ? lunchStartMinute.value : null,
      lunch_end_minute: lunchEnabled.value ? lunchEndMinute.value : null,
      channel: {},
    });
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.message ||
        t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <form class="mx-6 flex flex-col" @submit.prevent="updateInbox">
    <SettingsToggleSection
      v-model="isBusinessHoursEnabled"
      :header="t('INBOX_MGMT.BUSINESS_HOURS.TOGGLE_AVAILABILITY')"
      :description="t('INBOX_MGMT.BUSINESS_HOURS.TOGGLE_HELP')"
    >
      <template v-if="isBusinessHoursEnabled" #editor>
        <div class="mb-4 [&_.message-editor]:border-0">
          <WootMessageEditor
            v-if="isRichEditorEnabled"
            v-model="unavailableMessage"
            enable-variables
            is-format-mode
            :placeholder="
              t('INBOX_MGMT.BUSINESS_HOURS.UNAVAILABLE_MESSAGE_LABEL')
            "
            :min-height="4"
          />
          <textarea
            v-else
            v-model="unavailableMessage"
            class="min-h-[4rem] mt-1.5"
          />
        </div>
      </template>
    </SettingsToggleSection>
    <template v-if="isBusinessHoursEnabled">
      <div class="flex items-center my-6">
        <div class="flex-1 h-px bg-n-weak" />
        <span class="text-body-main text-n-slate-11 px-2">{{
          t('INBOX_MGMT.BUSINESS_HOURS.WEEKLY_TITLE')
        }}</span>
        <div class="flex-1 h-px bg-n-weak" />
      </div>
      <SettingsFieldSection
        :label="t('INBOX_MGMT.BUSINESS_HOURS.TIMEZONE_LABEL')"
      >
        <ComboBox
          v-model="timeZone"
          :options="timeZones"
          :placeholder="t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
          class="[&>div>button]:!bg-n-alpha-black2"
        />
      </SettingsFieldSection>
      <SettingsFieldSection
        :label="t('BUSINESS_HOURS_SETTINGS.LUNCH_LABEL')"
        :help-text="t('BUSINESS_HOURS_SETTINGS.LUNCH_HINT')"
      >
        <div class="flex flex-wrap items-center gap-3">
          <label class="flex items-center gap-2 text-body-main text-n-slate-12">
            <input
              v-model="lunchEnabled"
              type="checkbox"
              class="m-0 accent-n-brand dark:[color-scheme:dark]"
            />
            {{ t('BUSINESS_HOURS_SETTINGS.LUNCH_ENABLED') }}
          </label>
          <BusinessTimeInput
            v-model="lunchStart"
            :disabled="!lunchEnabled"
            :aria-label="t('BUSINESS_HOURS_SETTINGS.LUNCH_START')"
          />
          <Icon icon="i-lucide-minus" class="size-3 text-n-slate-11" />
          <BusinessTimeInput
            v-model="lunchEnd"
            :disabled="!lunchEnabled"
            :aria-label="t('BUSINESS_HOURS_SETTINGS.LUNCH_END')"
          />
        </div>
        <p v-if="!isLunchValid" class="m-0 mt-2 text-label-small text-n-ruby-9">
          {{ t('BUSINESS_HOURS_SETTINGS.LUNCH_VALIDATION') }}
        </p>
      </SettingsFieldSection>
      <div class="w-full overflow-x-auto mt-2">
        <table
          class="min-w-full table-auto outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
        >
          <thead>
            <tr class="border-b border-n-weak">
              <th
                class="py-2 ps-4 pe-3 text-start text-heading-3 text-n-slate-12"
              >
                {{ t('INBOX_MGMT.BUSINESS_HOURS.DAY.DAY') }}
              </th>
              <th class="py-2 pe-3 text-start text-heading-3 text-n-slate-12">
                {{ t('INBOX_MGMT.BUSINESS_HOURS.DAY.AVAILABILITY') }}
              </th>
              <th class="py-2 pe-3 text-start text-heading-3 text-n-slate-12">
                {{ t('INBOX_MGMT.BUSINESS_HOURS.DAY.HOURS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <BusinessDay
              v-for="slot in timeSlots"
              :key="slot.day"
              :day-name="dayNames[slot.day]"
              :time-slot="slot"
              :lunch-start-minute="effectiveLunchStart"
              :lunch-end-minute="effectiveLunchEnd"
              @update="data => onSlotUpdate(slot.day, data)"
            />
          </tbody>
        </table>
      </div>
    </template>
    <div class="flex justify-end items-center py-4 mt-2">
      <NextButton
        type="submit"
        :label="t('BUSINESS_HOURS_SETTINGS.SAVE')"
        :is-loading="uiFlags.isUpdating"
        :disabled="hasError"
      />
    </div>
  </form>
</template>
